import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import PageBanner from 'LuxKit/Banners/PageBanner'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import switchFunc from 'lib/function/switchFunc'
import { getDismissedBannerIds, isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import NotificationBannerModal from './NotificationBannerModal'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isBannerMessagePayload } from 'lib/lab/dynamicPayload'
import moment from 'moment'

const getBannerKind = switchFunc<React.ComponentProps<typeof PageBanner>['kind'], App.LabBannerMessagePayload['bannerType']>({
  warning: 'warning',
  error: 'critical',
  success: 'promotional-primary',
}, 'informational-primary')

const getBannerIcon = switchFunc<React.ComponentProps<typeof PageBanner>['icon'], App.LabBannerMessagePayload['bannerType']>({
  warning: <LineExclamationTriangleIcon />,
  error: <LineExclamationTriangleIcon />,
  success: <LineCheckCircleIcon />,
}, <LineInfoCircleIcon />)

const BANNER_DISMISSAL_PERIOD_DAYS = 7

export default function NotificationBannerV3() {
  const campaign = useLabFeatureFlag(LabFeatureFlags.enableTopBannerMessage)

  const dismissedBannerIds = getDismissedBannerIds()
  const banner = useMemo(() => {
    if (campaign?.enabled && isBannerMessagePayload(campaign.value) && !dismissedBannerIds.has(campaign.value.key)) {
      return campaign.value
    }
    return undefined
  }, [campaign, dismissedBannerIds])

  // TODO
  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    if (banner) {
      setIsDismissed(isBannerDismissed(banner.key))
    }
  }, [banner])

  useEffect(() => {
    if (banner) {
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          setIsDismissed(isBannerDismissed(banner.key))
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }, [banner])

  const containerRef = useRef<HTMLDivElement>(null)

  const showModal = useContext(ModalContext)

  const openBannerModal = useCallback(() => {
    if (banner) {
      showModal(<NotificationBannerModal title={banner.modalTitle} content={banner.modalContent} />)
    }
  }, [banner, showModal])

  const dismiss = useCallback(() => {
    if (banner) {
      setIsDismissed(true)
      // Dismiss endDate in BANNER_DISMISSAL_PERIOD_DAYS days from now
      recordSiteBannerDismissal(banner.key, moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString())
    }
  }, [banner])

  if (!banner || isDismissed) {
    return null
  }

  return <PageBanner
    ref={containerRef}
    id="notification-page-banner"
    className="no-print"
    kind={getBannerKind(banner.bannerType)}
    icon={getBannerIcon(banner.bannerType)}
    onDismiss={dismiss}
    tabletAlign="center"
    primaryActionButtonProps={banner.modalContent ? { onClick: openBannerModal, children: 'Read more' } : undefined}
  >
    <b>{banner.boldText}</b> {banner.bannerText}
  </PageBanner>
}
